/**
 * CR500: DealCardSpec v2 Loader
 * CR422: SSOT-aware loading from deal_card_config_versions via pointers
 * 
 * Loads spec from Supabase and resolves into a renderable spec.
 * Used by UI components to get the full resolved spec for a deal type.
 * 
 * Resolution priority:
 * 1. If environment is provided, try SSOT (config_pointers → config_versions)
 * 2. Fall back to legacy tables (deal_card_templates + deal_card_components_config)
 */

import { supabase } from "@/integrations/supabase/client";
import type {
  DealCardSpecV2,
  DealCardOverridesV2,
  ResolvedDealCardSpecV2,
  DealCardTemplateVariantRow,
  DealCardTemplateRowV2,
} from "./types";
import {
  parseSpecV2,
  parseOverridesV2,
  resolveDealCardSpecV2,
  DEFAULT_SPEC_V2,
} from "./resolveSpec";
import { normalizeComponentsShape } from "@/lib/deal-cards/normalizeComponents";
import { bridgeTemplateToV2Spec, type TemplateOverrideSpec } from "@/lib/deal-cards/templateBridge";

// ================================================================
// HELPERS
// ================================================================

/**
 * CR401: Deep merge two component objects.
 * Override components take precedence over base components.
 */
function deepMergeComponents(
  base: Record<string, unknown>,
  override: Record<string, unknown>
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...base };
  
  for (const [key, value] of Object.entries(override)) {
    const baseValue = result[key];
    
    // If both are objects, merge deeply
    if (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      baseValue !== null &&
      typeof baseValue === "object" &&
      !Array.isArray(baseValue)
    ) {
      result[key] = deepMergeComponents(
        baseValue as Record<string, unknown>,
        value as Record<string, unknown>
      );
    } else {
      // Otherwise override takes precedence
      result[key] = value;
    }
  }
  
  return result;
}

// ================================================================
// CACHE
// ================================================================

interface CachedSpec {
  spec: ResolvedDealCardSpecV2;
  timestamp: number;
  /** CR422: Track config source for debug visibility */
  source: SpecSource;
}

/** CR422: Metadata about where the spec was loaded from */
export interface SpecSource {
  type: "ssot" | "legacy" | "fallback";
  environment?: string;
  versionId?: string;
  /** Short version ID for display */
  versionShort?: string;
  dealType: string;
}

const specCache = new Map<string, CachedSpec>();
const CACHE_TTL_MS = 60_000; // 1 minute

/**
 * Clear the spec cache (useful after admin edits).
 */
export function clearSpecCache(): void {
  specCache.clear();
}

/**
 * Clear cache for a specific deal type.
 */
export function clearSpecCacheForType(dealTypeKey: string): void {
  // Clear all environment-keyed caches for this type
  const upperKey = dealTypeKey.toUpperCase();
  for (const key of [...specCache.keys()]) {
    if (key === upperKey || key.startsWith(`${upperKey}:`)) {
      specCache.delete(key);
    }
  }
}

// ================================================================
// CR422: SSOT LOADER (config_pointers → config_versions)
// ================================================================

/**
 * Build a cache key that includes environment when provided.
 */
function cacheKey(dealTypeKey: string, environment?: string): string {
  const upper = dealTypeKey.toUpperCase();
  return environment ? `${upper}:${environment}` : upper;
}

/**
 * CR422: Load spec from SSOT (deal_card_config_pointers → deal_card_config_versions).
 * Returns null if no SSOT config exists for this (environment, dealType).
 */
async function loadSpecFromSSOT(
  dealTypeKey: string,
  environment: string
): Promise<{ spec: ResolvedDealCardSpecV2; source: SpecSource } | null> {
  try {
    const { data, error } = await supabase.rpc(
      "get_published_deal_card_config",
      { p_environment: environment, p_deal_type: dealTypeKey },
    );

    if (error) {
      console.warn(`[loadSpecFromSSOT] RPC error for ${dealTypeKey}/${environment}:`, error);
      return null;
    }

    if (!data || (Array.isArray(data) && data.length === 0)) {
      return null;
    }

    const row = Array.isArray(data) ? data[0] : data;
    const configJson = (typeof row.config_json === "string"
      ? JSON.parse(row.config_json)
      : row.config_json) as Record<string, unknown>;

    if (!configJson) return null;

    const versionId = row.active_version_id as string;

    // First load the base variant spec (we still need this for structure)
    const { data: variantRow } = await supabase
      .from("deal_card_template_variants")
      .select("*")
      .eq("variant_key", "standard_v2")
      .maybeSingle();

    let variantSpec: DealCardSpecV2 | null = null;
    if (variantRow) {
      const typedVariant = variantRow as unknown as DealCardTemplateVariantRow;
      variantSpec = parseSpecV2(typedVariant.template_json);
    }

    // Resolve base spec without legacy overrides
    const baseSpec = resolveDealCardSpecV2(
      variantSpec,
      null,
      dealTypeKey,
      "standard_v2",
      { variant: variantSpec ? "db" : "fallback", overrides: "fallback" },
    );

    // Build template override from SSOT config_json
    const de = (configJson.data_elements ?? {}) as Record<string, unknown>;
    const rawComp = (configJson.components ?? {}) as Record<string, unknown>;
    const comp = normalizeComponentsShape(rawComp) ?? rawComp;

    const templateOverride: TemplateOverrideSpec = {
      sections: de.sections as Record<string, boolean> | undefined,
      labels: de.labels as Record<string, string> | undefined,
      header: de.header as Record<string, unknown> | undefined,
      extended_sections: de.extended_sections as Record<string, boolean> | undefined,
      styling: de.styling as Record<string, unknown> | undefined,
      section_order: de.section_order as string[] | undefined,
      theme_preset: de.theme_preset as string | undefined,
      surfaceOverrides: de.surfaceOverrides as TemplateOverrideSpec["surfaceOverrides"],
      components: comp,
    };

    // Apply SSOT overrides to base spec
    const resolvedSpec = bridgeTemplateToV2Spec(baseSpec, templateOverride);

    return {
      spec: resolvedSpec,
      source: {
        type: "ssot",
        environment,
        versionId,
        versionShort: versionId.slice(0, 8),
        dealType: dealTypeKey,
      },
    };
  } catch (err) {
    console.error(`[loadSpecFromSSOT] Unexpected error for ${dealTypeKey}/${environment}:`, err);
    return null;
  }
}

// ================================================================
// LOADER
// ================================================================

/**
 * Load the resolved DealCardSpec v2 for a given deal type.
 * 
 * CR422: When environment is provided, tries SSOT first (config_pointers → config_versions).
 * Falls back to legacy tables if no SSOT config exists.
 * 
 * Resolution path (SSOT):
 * 1. Query deal_card_config_pointers for (environment, deal_type)
 * 2. Load config_json from deal_card_config_versions
 * 3. Apply as template override to base variant spec
 * 
 * Resolution path (Legacy fallback):
 * 1. Query deal_card_templates for the deal type to get variant_key and overrides_json
 * 2. Query deal_card_template_variants for the variant template
 * 3. Merge components from deal_card_components_config
 * 4. Resolve into ResolvedDealCardSpecV2
 * 
 * Never throws - always returns a valid spec.
 */
export async function loadDealCardSpecV2(
  dealTypeKey: string,
  environment?: string
): Promise<ResolvedDealCardSpecV2> {
  const normalizedKey = dealTypeKey.toUpperCase();
  const key = cacheKey(normalizedKey, environment);
  
  // Check cache first
  const cached = specCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.spec;
  }
  
  // CR422: Try SSOT first when environment is provided
  if (environment) {
    const ssotResult = await loadSpecFromSSOT(normalizedKey, environment);
    if (ssotResult) {
      specCache.set(key, {
        spec: ssotResult.spec,
        timestamp: Date.now(),
        source: ssotResult.source,
      });
      return ssotResult.spec;
    }
    // No SSOT config — fall through to legacy
    console.log(`[loadDealCardSpecV2] No SSOT config for ${normalizedKey}/${environment}, falling back to legacy`);
  }
  
  // ================================================================
  // LEGACY PATH (original behavior)
  // ================================================================
  
  let variantSpec: DealCardSpecV2 | null = null;
  let overrides: DealCardOverridesV2 | null = null;
  let variantKey = "standard_v2";
  let variantSource: "db" | "fallback" = "fallback";
  let overridesSource: "db" | "fallback" = "fallback";
  
  // CR306: Track legacy styling for normalization shim
  let legacyStyling: Record<string, unknown> | null = null;
  // CR325: Track components for v2 rendering
  let components: Record<string, unknown> | null = null;
  
  try {
    // Step 1: Get template row + components config for this deal type
    const [templateResult, componentsConfigResult] = await Promise.all([
      supabase
        .from("deal_card_templates")
        .select("deal_type_key, variant_key, overrides_json, legacy_template_json, template_json")
        .eq("deal_type_key", normalizedKey)
        .maybeSingle(),
      supabase
        .from("deal_card_components_config")
        .select("components_json")
        .eq("deal_type_key", normalizedKey)
        .eq("variant_key", "standard_v2")
        .maybeSingle(),
    ]);
    
    const { data: templateRow, error: templateError } = templateResult;
    
    if (templateError) {
      console.warn(`[loadDealCardSpecV2] Template query error for ${normalizedKey}:`, templateError);
    }
    
    if (templateRow) {
      // Parse overrides
      const typedRow = templateRow as unknown as DealCardTemplateRowV2;
      overrides = parseOverridesV2(typedRow.overrides_json);
      if (overrides) {
        overridesSource = "db";
      }
      
      // Get variant key
      if (typedRow.variant_key) {
        variantKey = typedRow.variant_key;
      }
      
      // CR306: Extract legacy styling for normalization
      const templateJson = typedRow.template_json as Record<string, unknown> | null;
      if (templateJson?.styling) {
        legacyStyling = templateJson.styling as Record<string, unknown>;
      }
      
      // CR325: Extract components for v2 rendering from template_json
      if (templateJson?.components) {
        components = templateJson.components as Record<string, unknown>;
      }
      
      // CR401: Merge components from overrides_json (ribbon styling, etc.)
      const overridesJson = typedRow.overrides_json as Record<string, unknown> | null;
      if (overridesJson?.components) {
        const overrideComponents = overridesJson.components as Record<string, unknown>;
        components = components 
          ? deepMergeComponents(components, overrideComponents) 
          : overrideComponents;
      }
    }
    
    // CR418/CR421: Merge components from deal_card_components_config (highest priority)
    // CR421: Normalize nested components.components shape before merging
    if (componentsConfigResult.data) {
      const compJson = componentsConfigResult.data.components_json as Record<string, unknown> | null;
      const normalizedComp = normalizeComponentsShape(compJson);
      if (normalizedComp && Object.keys(normalizedComp).length > 0) {
        components = components
          ? deepMergeComponents(components, normalizedComp)
          : normalizedComp;
      }
    }
    
    // Step 2: Get variant template
    const { data: variantRow, error: variantError } = await supabase
      .from("deal_card_template_variants")
      .select("*")
      .eq("variant_key", variantKey)
      .maybeSingle();
    
    if (variantError) {
      console.warn(`[loadDealCardSpecV2] Variant query error for ${variantKey}:`, variantError);
    }
    
    if (variantRow) {
      const typedVariant = variantRow as unknown as DealCardTemplateVariantRow;
      variantSpec = parseSpecV2(typedVariant.template_json);
      if (variantSpec) {
        variantSource = "db";
      }
    }
  } catch (err) {
    console.error(`[loadDealCardSpecV2] Unexpected error for ${normalizedKey}:`, err);
  }
  
  // Step 3: Resolve the final spec (with legacy styling for normalization + components)
  const resolved = resolveDealCardSpecV2(
    variantSpec,
    overrides,
    normalizedKey,
    variantKey,
    { variant: variantSource, overrides: overridesSource },
    legacyStyling,
    components
  );
  
  // Cache the result
  specCache.set(key, {
    spec: resolved,
    timestamp: Date.now(),
    source: {
      type: "legacy",
      dealType: normalizedKey,
      environment: environment ?? undefined,
    },
  });
  
  return resolved;
}

/**
 * CR422: Get the spec source metadata for a cached spec.
 * Returns null if not cached.
 */
export function getCachedSpecSource(dealTypeKey: string, environment?: string): SpecSource | null {
  const key = cacheKey(dealTypeKey.toUpperCase(), environment);
  const cached = specCache.get(key);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.source;
  }
  return null;
}

/**
 * Load specs for all known deal types at once.
 * More efficient than individual calls for initial load.
 */
export async function loadAllDealCardSpecs(): Promise<Map<string, ResolvedDealCardSpecV2>> {
  const dealTypes = ["BONUS", "DISCOUNT", "POINTS", "IN_KIND", "UNCLASSIFIED"];
  const results = new Map<string, ResolvedDealCardSpecV2>();
  
  try {
    // Batch query all templates, variants, and components config
    const [templatesResult, variantsResult, componentsConfigResult] = await Promise.all([
      supabase
        .from("deal_card_templates")
        .select("deal_type_key, variant_key, overrides_json, legacy_template_json, template_json"),
      supabase
        .from("deal_card_template_variants")
        .select("*"),
      supabase
        .from("deal_card_components_config")
        .select("deal_type_key, components_json"),
    ]);
    
    // Build variant lookup
    const variantMap = new Map<string, DealCardSpecV2 | null>();
    if (!variantsResult.error && variantsResult.data) {
      for (const row of variantsResult.data) {
        const typedRow = row as unknown as DealCardTemplateVariantRow;
        variantMap.set(typedRow.variant_key, parseSpecV2(typedRow.template_json));
      }
    }
    
    // Build template lookup
    const templateMap = new Map<string, DealCardTemplateRowV2>();
    if (!templatesResult.error && templatesResult.data) {
      for (const row of templatesResult.data) {
        const typedRow = row as unknown as DealCardTemplateRowV2;
        templateMap.set(typedRow.deal_type_key, typedRow);
      }
    }
    
    // CR418: Build components config lookup
    const componentsConfigMap = new Map<string, Record<string, unknown>>();
    if (!componentsConfigResult.error && componentsConfigResult.data) {
      for (const row of componentsConfigResult.data) {
        const typedRow = row as { deal_type_key: string; components_json: Record<string, unknown> | null };
        if (typedRow.components_json) {
          componentsConfigMap.set(typedRow.deal_type_key, typedRow.components_json);
        }
      }
    }
    
    // Resolve each deal type
    for (const dealType of dealTypes) {
      const template = templateMap.get(dealType);
      const variantKey = template?.variant_key || "standard_v2";
      const variantSpec = variantMap.get(variantKey) || null;
      const overrides = template ? parseOverridesV2(template.overrides_json) : null;
      
      // CR306: Extract legacy styling for normalization
      const templateJson = template?.template_json as Record<string, unknown> | null;
      const legacyStyling = templateJson?.styling as Record<string, unknown> | null;
      
      // CR325: Extract components for v2 rendering
      let components = templateJson?.components as Record<string, unknown> | null;
      
      // CR401: Merge components from overrides_json (ribbon styling, etc.)
      const overridesJson = template?.overrides_json as Record<string, unknown> | null;
      if (overridesJson?.components) {
        const overrideComponents = overridesJson.components as Record<string, unknown>;
        components = components 
          ? deepMergeComponents(components, overrideComponents) 
          : overrideComponents;
      }
      
      // CR418/CR421: Merge components from deal_card_components_config (highest priority)
      const compConfigJson = componentsConfigMap.get(dealType);
      const normalizedComp = normalizeComponentsShape(compConfigJson ?? null);
      if (normalizedComp && Object.keys(normalizedComp).length > 0) {
        components = components
          ? deepMergeComponents(components, normalizedComp)
          : normalizedComp;
      }
      
      const resolved = resolveDealCardSpecV2(
        variantSpec,
        overrides,
        dealType,
        variantKey,
        {
          variant: variantSpec ? "db" : "fallback",
          overrides: overrides ? "db" : "fallback",
        },
        legacyStyling,
        components
      );
      
      results.set(dealType, resolved);
      
      // Update cache
      specCache.set(dealType, {
        spec: resolved,
        timestamp: Date.now(),
        source: { type: "legacy", dealType },
      });
    }
  } catch (err) {
    console.error("[loadAllDealCardSpecs] Error loading specs:", err);
    
    // Return fallback specs for all types
    for (const dealType of dealTypes) {
      const fallback = resolveDealCardSpecV2(
        null,
        null,
        dealType,
        "standard_v2",
        { variant: "fallback", overrides: "fallback" }
      );
      results.set(dealType, fallback);
    }
  }
  
  return results;
}

/**
 * Check if the v2 spec system is enabled.
 * Reads from app_config table.
 */
export async function isDealCardSpecV2Enabled(): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("app_config")
      .select("value_json")
      .eq("key", "dealCardSpecV2_enabled")
      .maybeSingle();
    
    if (error) {
      console.warn("[isDealCardSpecV2Enabled] Query error:", error);
      return false;
    }
    
    return data?.value_json === true;
  } catch {
    return false;
  }
}

// ================================================================
// SYNC LOADER (for cached access)
// ================================================================

/**
 * Get a cached spec synchronously.
 * Returns null if not cached - use loadDealCardSpecV2 first.
 */
export function getCachedSpec(dealTypeKey: string): ResolvedDealCardSpecV2 | null {
  const cached = specCache.get(dealTypeKey.toUpperCase());
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.spec;
  }
  return null;
}

/**
 * Get a fallback spec for immediate use (no DB call).
 * Use when you need a spec immediately and can't await.
 */
export function getFallbackSpec(dealTypeKey: string): ResolvedDealCardSpecV2 {
  return resolveDealCardSpecV2(
    null,
    null,
    dealTypeKey.toUpperCase(),
    "standard_v2",
    { variant: "fallback", overrides: "fallback" }
  );
}
